import { useState } from 'react';
import { Pin as PinIcon, ChevronDown, ChevronUp, X } from 'lucide-react';
import type { Pin } from '../../../api/chat';
import Avatar from './Avatar';

function fmtRelativeTime(iso: string): string {
  const now = Date.now();
  const then = new Date(iso).getTime();
  const diffS = Math.max(0, Math.floor((now - then) / 1000));
  if (diffS < 60) return 'just now';
  const diffM = Math.floor(diffS / 60);
  if (diffM < 60) return `${diffM}m ago`;
  const diffH = Math.floor(diffM / 60);
  if (diffH < 24) return `${diffH}h ago`;
  const diffD = Math.floor(diffH / 24);
  if (diffD < 7) return `${diffD}d ago`;
  return new Date(iso).toLocaleDateString([], { day: 'numeric', month: 'short' });
}

function previewOf(pin: Pin): string {
  const m = pin.message;
  if (!m) return '(message unavailable)';
  if (m.is_deleted) return '(deleted message)';
  if (m.content && m.content.trim()) return m.content;
  if (m.attachments && m.attachments.length) return `📎 ${m.attachments[0].file_name || 'Attachment'}`;
  if (m.message_type === 'file') return '📎 File';
  if (m.message_type === 'image') return '🖼️ Image';
  if (m.message_type === 'voice') return '🎤 Voice message';
  return '';
}

function PinnedBar({
  pins,
  onUnpin,
  onJump,
}: {
  pins: Pin[];
  onUnpin: (messageId: string) => void;
  onJump: (messageId: string) => void;
}) {
  const [expanded, setExpanded] = useState(false);

  if (!pins.length) return null;

  return (
    <div className="bg-amber-50 border-b border-amber-200 text-[13px] flex-shrink-0">
      <button
        type="button"
        onClick={() => setExpanded(v => !v)}
        className="w-full flex items-center gap-2 px-4 py-2 hover:bg-amber-100/60 transition-colors text-left"
      >
        <PinIcon size={13} className="text-amber-600 flex-shrink-0" />
        <span className="font-semibold text-amber-800">
          {pins.length} pinned message{pins.length === 1 ? '' : 's'}
        </span>
        <span className="ml-auto text-amber-600">
          {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
        </span>
      </button>

      {expanded && (
        <div className="border-t border-amber-200 max-h-64 overflow-y-auto">
          {pins.map(pin => {
            const authorName = pin.message?.sender_name || pin.pinned_by_name || 'Unknown';
            return (
              <div
                key={pin.id}
                role="button"
                tabIndex={0}
                onClick={() => onJump(pin.message_id)}
                onKeyDown={e => { if (e.key === 'Enter') onJump(pin.message_id); }}
                className="group flex items-center gap-2.5 px-4 py-2 hover:bg-amber-100/70 transition-colors cursor-pointer border-b border-amber-100 last:border-b-0"
              >
                <Avatar name={authorName} size={7} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-semibold text-gray-800 truncate">{authorName}</span>
                    <span className="text-[10px] text-gray-500 flex-shrink-0">
                      pinned {fmtRelativeTime(pin.pinned_at)}
                    </span>
                  </div>
                  <p className="text-xs text-gray-700 line-clamp-1">
                    {previewOf(pin)}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={e => { e.stopPropagation(); onUnpin(pin.message_id); }}
                  className="opacity-0 group-hover:opacity-100 p-1 text-amber-600 hover:text-red-600 hover:bg-white rounded-lg transition-colors flex-shrink-0 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  title="Unpin"
                >
                  <X size={13} />
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default PinnedBar;
